<template>
  <div class="warehouse-form">
    <div class="card" style="max-width: 980px;">
      <form class="form-grid" @submit.prevent="submitDamage">
        <div class="card-header">
          <div>
            <h2 class="title" style="font-size: 22px;">Relatar avaria</h2>
            <p class="subtitle">Preencha os dados para abrir o registro de avaria.</p>
          </div>
        </div>

        <div v-if="item" class="item-card item-summary">
          <div class="item-photo-wrap">
            <img v-if="item.photoDataUrl" class="item-photo" :src="$resolveUrl(item.photoDataUrl)" alt="foto item" />
            <div v-else class="item-photo item-photo-empty">Sem foto</div>
          </div>
          <div>
            <strong>{{ item.name }}</strong>
            <div class="item-meta">ID: {{ item.code }}</div>
            <div class="item-meta">Categoria: {{ item.category || '-' }}</div>
          </div>
        </div>

        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
          <div class="input-field">
            <label for="damage-summary">Resumo da avaria</label>
            <input id="damage-summary" v-model="form.damageSummary" type="text" />
          </div>
          <div class="input-field">
            <label for="damage-project">Projeto</label>
            <select id="damage-project" v-model="form.projectId">
              <option value="">Selecione</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
            </select>
          </div>
          <div class="input-field">
            <label for="damage-collaborator">Colaborador envolvido</label>
            <select id="damage-collaborator" v-model="form.collaboratorUsername">
              <option value="">Selecione</option>
              <option v-for="user in collaborators" :key="user.username" :value="user.username">
                {{ user.name }} ({{ user.username }})
              </option>
            </select>
          </div>
        </div>

        <div class="input-field">
          <label for="damage-details">Descricao detalhada da avaria</label>
          <textarea id="damage-details" v-model="form.damageDetails" rows="4"></textarea>
        </div>

        <div class="input-field">
          <label>Fotos da avaria (multiplas)</label>
          <div
            class="image-grid"
            :class="{ 'is-drag-over': isDragOver }"
            @dragenter.prevent="isDragOver = true"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="onImagesDropped"
          >
            <div v-for="(img, index) in form.photoDataUrls" :key="`damage-img-${index}`" class="image-preview">
              <img :src="resolveUrl(img)" alt="preview avaria" />
              <button class="image-remove" type="button" @click="removeImage(index)">x</button>
            </div>
            <button class="image-add" type="button" @click="triggerImageInput">+</button>
          </div>
          <input id="damage-file-input" class="hidden-input" type="file" accept="image/*" multiple @change="onImagesSelected" />
        </div>

        <p v-if="error" class="subtitle" style="color: #a54b2a;">{{ error }}</p>

        <div class="actions">
          <button class="btn btn-primary" type="submit" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar avaria' }}</button>
          <button class="btn btn-ghost" type="button" :disabled="saving" @click="cancel">Cancelar</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '@/store/api'
import { projectsStore } from '@/store/projects'
import { warehouseStore } from '@/store/warehouse'
import { resolveUrl } from '@/utils/resolveUrl'

export default defineComponent({
  name: 'WarehouseDamageReportForm',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const collaborators = ref<Array<{ username: string; name: string }>>([])
    const error = ref('')
    const saving = ref(false)
    const isDragOver = ref(false)

    const form = ref({
      itemId: '',
      damageSummary: '',
      damageDetails: '',
      projectId: '',
      collaboratorUsername: '',
      photoDataUrls: [] as string[]
    })

    const item = computed(() => warehouseStore.all().find((entry) => entry.id === form.value.itemId) || null)
    const projects = computed(() => projectsStore.all())

    const processFiles = (files: File[]) => {
      const valid = files.filter((file) => file.type.startsWith('image/'))
      valid.forEach((file) => {
        const reader = new FileReader()
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            form.value.photoDataUrls.push(reader.result)
          }
        }
        reader.readAsDataURL(file)
      })
    }

    const triggerImageInput = () => {
      const input = document.getElementById('damage-file-input') as HTMLInputElement | null
      if (input) input.click()
    }

    const onImagesSelected = (event: Event) => {
      const target = event.target as HTMLInputElement
      const files = target.files ? Array.from(target.files) : []
      processFiles(files)
      target.value = ''
    }

    const onImagesDropped = (event: DragEvent) => {
      isDragOver.value = false
      const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []
      processFiles(files)
    }

    const removeImage = (index: number) => {
      form.value.photoDataUrls.splice(index, 1)
    }

    const submitDamage = async () => {
      error.value = ''
      if (!form.value.itemId || !form.value.damageSummary.trim() || !form.value.damageDetails.trim()) {
        error.value = 'Preencha item, resumo e descricao detalhada.'
        return
      }
      const itemLabel = item.value ? item.value.name : form.value.itemId
      saving.value = true
      try {
        await apiFetch('/tickets', {
          method: 'POST',
          body: JSON.stringify({
            title: `Avaria em ferramenta: ${itemLabel}`,
            category: 'manutencao-ferramentas',
            priority: 'alta',
            description: form.value.damageDetails.trim(),
            toolItemId: form.value.itemId,
            projectId: form.value.projectId,
            damageSummary: form.value.damageSummary.trim(),
            damageDetails: form.value.damageDetails.trim(),
            collaboratorUsername: form.value.collaboratorUsername,
            photoDataUrls: form.value.photoDataUrls
          })
        })
        await router.push('/almoxarifado/avarias')
      } finally {
        saving.value = false
      }
    }

    const cancel = () => {
      router.push('/almoxarifado/avarias')
    }

    onMounted(async () => {
      const itemId = typeof route.query.item === 'string' ? route.query.item : ''
      form.value.itemId = itemId
      await Promise.all([
        warehouseStore.refresh(),
        projectsStore.refresh(),
        apiFetch<Array<{ username: string; name: string }>>('/accounts').then((rows) => {
          collaborators.value = rows.map((row) => ({
            username: String(row.username || ''),
            name: String(row.name || row.username || '')
          }))
        })
      ]).catch(() => {
        error.value = 'Falha ao carregar dados para relatar avaria.'
      })
    })

    return {
      form,
      item,
      projects,
      collaborators,
      error,
      saving,
      isDragOver,
      resolveUrl,
      triggerImageInput,
      onImagesSelected,
      onImagesDropped,
      removeImage,
      submitDamage,
      cancel
    }
  }
})
</script>

<style scoped lang="scss">
.warehouse-form {
  width: 100%;
}

.item-summary {
  grid-template-columns: 120px 1fr;
}

.item-photo-wrap {
  width: 110px;
  height: 110px;
  overflow: hidden;
}

.item-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
}

.item-photo-empty {
  display: grid;
  place-items: center;
  background: #ececec;
  color: #565656;
  font-size: 12px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 10px;
  padding: 6px;
  border: 1px dashed transparent;
  border-radius: 12px;
}

.image-grid.is-drag-over {
  border-color: var(--system-navbar, #4f3300);
  background: color-mix(in srgb, var(--system-navbar-text, #f3ede3) 18%, transparent);
}

.image-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(31, 30, 26, 0.14);
  aspect-ratio: 1 / 1;
  width: 108px;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  border-radius: 8px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  cursor: pointer;
}

.image-add {
  width: 108px;
  height: 108px;
  border-radius: 12px;
  border: 1px dashed rgba(31, 30, 26, 0.2);
  display: grid;
  place-items: center;
  font-size: 32px;
  color: var(--accent, #0f6b6e);
  background: rgba(15, 107, 110, 0.08);
  cursor: pointer;
}
</style>
